import api from '@/api'
import store from 'store'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import _ from 'lodash'
dayjs.extend(relativeTime)

const defaultSettings = {
  loaded: false,
  systems: [],
  email: { smtp: {} }
}

export default {
  state: {
    info: {},
    settingsCache: store.get('ews_station_settings', {
      inews: {},
      enps: {},
      smartsheet: { today: {}, future: {}, archive: {} },
      catdv: {},
      filesystem: {},
      email: { smtp: {} },
      contactColumns: []
    }),
    settings: _.cloneDeep(defaultSettings),
    groups: {
      reporter: { id: '', extra: '' },
      photog: { id: '', extra: '' },
      producer: { id: '', extra: '' },
      editor: { id: '', extra: '' },
      pitchedBy: {}
    },
    form: {
      settingsLoaded: false,
      validFile: false,
      saving: false,
      unsavedChanges: false,
      deployNeeded: false
    },
    logs: [],
    stagehandSystems: [],
    stagehandJobs: [],
    status: {
      debug: 0,
      info: 0,
      warn: 0,
      errors: 0
    }
  },
  mutations: {
    SET_STATUS(state, status) {
      state.status = status
    },
    SET_LOGS(state, logs) {
      state.logs = logs
    },
    UNSAVED(state) {
      state.form.unsavedChanges = true
    },
    SAVING(state) {
      state.form.saving = true
    },
    SAVED(state) {
      state.form.saving = false
      state.form.unsavedChanges = false
      state.form.deployNeeded = true
    },
    LOAD_STAGEHAND_INFO(state, info) {
      state.info = info
    },
    UNLOAD_STAGEHAND_SETTINGS(state) {
      state.form.settingsLoaded = false
      state.settings = _.cloneDeep(defaultSettings)
      store.set('ews_station_settings', state.settings)
      state.form.validFile = false
    },
    LOAD_STAGEHAND_SETTINGS(state, settings) {
      state.form.settingsLoaded = true
      if (settings.systems) {
        // ensure systems that should have a shows array do
        if (settings.systems)
          settings.systems.forEach((s) => {
            if (['inews', 'enps', 'softron', 'brightspot'].includes(s.type) && !s.shows) s.shows = []
            if (['inews', 'enps', 'softron', 'brightspot'].includes(s.type) && !s.formatMap) s.formatMap = []
          })
        state.settings = settings
        // set local store
        store.set('ews_station_settings', settings)
        state.form.validFile = true
      }

      setTimeout(() => {
        state.form.unsavedChanges = false
      }, 150)
    },
    GET_STAGEHAND_STATUS(state, systems) {
      state.stagehandSystems = systems
    },
    GET_STAGEHAND_JOBS(state, jobs) {
      state.stagehandJobs = jobs
    },
    ENABLE_FORM(state) {
      state.form.unsavedChanges = true
    },
    DISABLE_FORM(state) {
      state.form.unsavedChanges = false
      state.form.saving = false
      state.form.deployNeeded = true
    },
    RESET_DEPLOY_NEEDED(state) {
      state.form.deployNeeded = false
    }
  },
  actions: {
    resetDeployNeeded({ commit }) {
      commit('RESET_DEPLOY_NEEDED')
    },
    async getStagehandStatus({ commit }) {
      let response = await api.newsdesk.getStagehandSystems()
      commit('GET_STAGEHAND_STATUS', response?.data?.data ?? [])
    },
    async getStagehandJobs({ commit }) {
      let response = await api.newsdesk.getStagehandJobs()
      commit('GET_STAGEHAND_JOBS', response?.data)
    },
    async loadStagehandInfo({ commit }) {
      let response = await api.newsdesk.getStagehandInfo()
      commit('LOAD_STAGEHAND_INFO', response?.data)
    },
    // ONLY RUNS FOR STAGEHAND USERS
    loadStagehandSettings({ commit }) {
      commit('UNLOAD_STAGEHAND_SETTINGS')
      api.newsdesk
        .getStagehandSettings()
        .then((response) => {
          const settings = response.data
          if (_.isEmpty(settings)) console.warn('empty settings')
          else commit('LOAD_STAGEHAND_SETTINGS', response.data)
        })
        .catch((error) => {
          throw new Error(`loadStagehandSettings ${error}`)
        })
    },
    async loadStandardLogs({ commit }, station, level) {
      const resp = await api.newsdesk.getStandardLogs(station, level)

      let warn = 0
      let debug = 0
      let info = 0
      let errors = 0

      const hits = resp?.data?.data ?? []

      hits.forEach((hit) => {
        switch (hit.attributes.status) {
          case 'warn':
            warn++
            break

          case 'info':
            info++
            break

          case 'debug':
            debug++
            break

          default:
            errors++
            break
        }
      })

      commit('SET_STATUS', { warn: warn, info: info, errors: errors, debug: debug })
      const _hits = resp?.data?.data ?? []

      let logs = _hits.map((hit) => {
        let date = dayjs(hit.attributes.timestamp)
        hit.time = date.format('MMM D HH:mm:ss')
        hit.msg = hit.attributes.message
        hit.level = hit.attributes.status
        return hit
      })

      commit('SET_LOGS', logs)
    },
    saveSettings({ state, commit }) {
      state.form.saving = true

      // convert groups to contactColumns array before sending api update
      let contacts = []

      contacts.push({
        name: 'Reporter',
        groups: state.groups.reporter.id.split(',').map((item) => item.trim()),
        extra: state.groups.reporter.extra.split(',').map((item) => item.trim())
      })

      contacts.push({
        name: 'Producer',
        groups: state.groups.producer.id.split(',').map((item) => item.trim()),
        extra: state.groups.producer.extra.split(',').map((item) => item.trim())
      })

      contacts.push({
        name: 'Editor',
        groups: state.groups.editor.id.split(',').map((item) => item.trim()),
        extra: state.groups.editor.extra.split(',').map((item) => item.trim())
      })

      contacts.push({
        name: 'Photog',
        groups: state.groups.photog.id.split(',').map((item) => item.trim()),
        extra: state.groups.photog.extra.split(',').map((item) => item.trim())
      })

      let data = state.settings

      api.newsdesk
        .saveStagehandSettings(data)
        .then((response) => {
          commit('DISABLE_FORM')
        })
        .catch((error) => {
          throw new Error(`API ${error}`)
        })
    },
    enableForm({ commit }) {
      commit('ENABLE_FORM')
    },
    async stopSystem({ commit, dispatch }, systemName) {
      await api.newsdesk.stopStagehandSystem(systemName)
      await dispatch('getStagehandStatus')
    },
    async startSystem({ commit, dispatch }, systemName) {
      await api.newsdesk.startStagehandSystem(systemName)
      await dispatch('getStagehandStatus')
    }
  },
  getters: {
    contactGroups(state) {
      return state.groups
    },
    stagehand(state) {
      return state.settings
    },
    stagehandInfo(state) {
      return state.info
    },
    stagehandSystems(state) {
      return state.stagehandSystems
    },
    stagehandJobs(state) {
      return state.stagehandJobs
    },
    deployNeeded(state) {
      return state.form.deployNeeded
    },
    hasStagehandConfig(state) {
      if (state.form?.settingsLoaded) return true
      return false
    }
  }
}
